import React, { useState } from 'react';
import { BarChart3, Mail, Lock, User, Eye, EyeOff, ArrowLeft, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// ─── View states ─────────────────────────────────────────────────────────────
// 'login'    — sign-in form
// 'signup'   — create account form
// 'forgot'   — send password reset email
// 'check'    — post-signup "verify your email" screen
// 'pwcheck'  — post-forgot "check your email" confirmation

export const Login = () => {
  const { signIn, signUp, resetPasswordForEmail, resendConfirmationEmail } = useAuth();
  const [pendingEmail, setPendingEmail] = useState('');
  const [view, setView] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    username: '',
  });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const reset = (nextView) => {
    setError('');
    setMessage('');
    setResendMessage('');
    setFormData({ email: '', password: '', confirmPassword: '', username: '' });
    setView(nextView);
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendMessage('');
    const { error } = await resendConfirmationEmail(pendingEmail);
    setResendLoading(false);
    setResendMessage(error ? error.message : 'Verification email resent! Check your inbox.');
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ── Submit handler ──────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      if (view === 'signup') {
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          return;
        }
        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          return;
        }

        const { data, error } = await signUp(
          formData.email,
          formData.password,
          formData.username || formData.email.split('@')[0],
        );

        if (error) {
          const msg = error.message.toLowerCase();
          if (msg.includes('already registered') || msg.includes('already exists')) {
            setError('An account with this email already exists. Try signing in instead.');
          } else if (msg.includes('rate limit') || msg.includes('rate_limit') || error.status === 429) {
            setError('Too many signup attempts. Please wait a few minutes and try again.');
          } else {
            setError(error.message);
          }
        } else if (data?.session) {
          // Email confirmation is disabled in Supabase — user is already signed in.
          // AuthContext will detect the session and route them to SubscriptionGate.
          // Nothing to do here; just let the auth state propagate.
        } else {
          // Email confirmation is enabled — verification email has been sent.
          setPendingEmail(formData.email);
          setView('check');
        }
      } else if (view === 'login') {
        const { error } = await signIn(formData.email, formData.password);
        if (error) {
          if (error.message.toLowerCase().includes('invalid login') ||
              error.message.toLowerCase().includes('invalid credentials')) {
            setError('Incorrect email or password. Please try again.');
          } else {
            setError(error.message);
          }
        }
      } else if (view === 'forgot') {
        const { error } = await resetPasswordForEmail(formData.email);
        if (error) {
          setError(error.message);
        } else {
          setView('pwcheck');
        }
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Post-signup: verify your email ──────────────────────────────────────────
  if (view === 'check') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center p-4">
        <div className="bg-gray-900/80 backdrop-blur-xl rounded-3xl p-10 w-full max-w-md border border-gray-700/50 text-center">
          <div className="flex items-center justify-center mb-6">
            <BarChart3 className="h-14 w-14 text-orange-500 mr-3" />
            <h1 className="text-4xl font-black text-white">
              Hoops<span className="text-orange-500">Analyst</span>
            </h1>
          </div>
          <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="h-8 w-8 text-orange-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Verify your email</h2>
          <p className="text-gray-400 mb-2">
            We sent a verification link to
          </p>
          <p className="text-white font-semibold mb-6">{pendingEmail}</p>
          <p className="text-gray-400 text-sm mb-8">
            Click the link in the email to confirm your account, then you'll be taken to subscribe.
            <br />
            <span className="text-gray-500">Don't forget to check your spam folder.</span>
          </p>

          {/* Resend */}
          {resendMessage ? (
            <p className={`text-sm mb-6 ${resendMessage.startsWith('Verification') ? 'text-green-400' : 'text-red-400'}`}>
              {resendMessage}
            </p>
          ) : (
            <button
              onClick={handleResend}
              disabled={resendLoading}
              className="inline-flex items-center gap-2 text-sm text-orange-400 hover:text-orange-300 disabled:opacity-50 transition-colors mb-6"
            >
              <RefreshCw className={`h-4 w-4 ${resendLoading ? 'animate-spin' : ''}`} />
              {resendLoading ? 'Sending…' : "Didn't get it? Resend verification email"}
            </button>
          )}

          <button
            onClick={() => reset('login')}
            className="text-gray-400 hover:text-gray-300 transition-colors flex items-center gap-2 mx-auto text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  // ── Post-forgot-password: check your email ───────────────────────────────────
  if (view === 'pwcheck') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center p-4">
        <div className="bg-gray-900/80 backdrop-blur-xl rounded-3xl p-10 w-full max-w-md border border-gray-700/50 text-center">
          <div className="flex items-center justify-center mb-6">
            <BarChart3 className="h-14 w-14 text-orange-500 mr-3" />
            <h1 className="text-4xl font-black text-white">
              Hoops<span className="text-orange-500">Analyst</span>
            </h1>
          </div>
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="h-8 w-8 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Check your inbox</h2>
          <p className="text-gray-400 mb-6">
            We sent a password reset link to{' '}
            <span className="text-white font-medium">{formData.email}</span>.
            Click the link in the email to set a new password.
          </p>
          <button
            onClick={() => reset('login')}
            className="text-orange-400 hover:text-orange-300 transition-colors flex items-center gap-2 mx-auto"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  // ── Main form ───────────────────────────────────────────────────────────────
  const isForgot = view === 'forgot';
  const isSignUp = view === 'signup';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center p-4">
      <div className="bg-gray-900/80 backdrop-blur-xl rounded-3xl p-10 w-full max-w-md border border-gray-700/50">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center mb-6">
            <BarChart3 className="h-14 w-14 text-orange-500 mr-3" />
            <h1 className="text-4xl font-black text-white">
              Hoops<span className="text-orange-500">Analyst</span>
            </h1>
          </div>
          <p className="text-gray-400 text-lg">
            {isForgot ? 'Reset your password' : isSignUp ? 'Create your account' : 'Welcome back'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email */}
          <div className="relative">
            <Mail className="absolute left-4 top-4 h-6 w-6 text-gray-400" />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full pl-12 pr-4 py-4 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Enter your email"
              required
            />
          </div>

          {/* Username (signup only) */}
          {isSignUp && (
            <div className="relative">
              <User className="absolute left-4 top-4 h-6 w-6 text-gray-400" />
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className="w-full pl-12 pr-4 py-4 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Choose a username (optional)"
              />
            </div>
          )}

          {/* Password (not shown on forgot screen) */}
          {!isForgot && (
            <div className="relative">
              <Lock className="absolute left-4 top-4 h-6 w-6 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full pl-12 pr-12 py-4 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-4 text-gray-400 hover:text-white"
              >
                {showPassword ? <EyeOff className="h-6 w-6" /> : <Eye className="h-6 w-6" />}
              </button>
            </div>
          )}

          {/* Confirm Password (signup only) */}
          {isSignUp && (
            <div className="relative">
              <Lock className="absolute left-4 top-4 h-6 w-6 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="w-full pl-12 pr-4 py-4 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Confirm your password"
                required
              />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 text-red-300 text-sm">
              {error}
            </div>
          )}

          {/* Success */}
          {message && (
            <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-4 text-green-300 text-sm">
              {message}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                {isForgot ? 'Sending...' : isSignUp ? 'Creating Account...' : 'Signing In...'}
              </>
            ) : (
              isForgot ? 'Send Reset Link' : isSignUp ? 'Create Account' : 'Sign In'
            )}
          </button>

          {/* Back link on forgot view */}
          {isForgot && (
            <button
              type="button"
              onClick={() => reset('login')}
              className="w-full text-gray-400 hover:text-orange-400 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to sign in
            </button>
          )}

          {/* Forgot password link (login view only) */}
          {!isForgot && !isSignUp && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => reset('forgot')}
                className="text-orange-400 hover:text-orange-300 transition-colors text-sm font-medium"
              >
                Forgot your password?
              </button>
            </div>
          )}

          {/* Toggle login ↔ signup */}
          {!isForgot && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => reset(isSignUp ? 'login' : 'signup')}
                className="text-gray-400 hover:text-orange-500 transition-colors"
              >
                {isSignUp
                  ? 'Already have an account? Sign in'
                  : "Don't have an account? Sign up"}
              </button>
            </div>
          )}
        </form>

        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">Advanced Basketball Analytics Platform</p>
        </div>
      </div>
    </div>
  );
};
